import { BASEMAPS } from '@mighty-map/map-engine';
import { KeyRound, LogOut, Settings2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { initials } from '../../lib/brand';
import { useMapStore } from '../../state/mapStore';
import { useSession } from '../../state/sessionStore';

/** The signed-in person: a round initials button in the header, opening who-am-I, basemap and sign-out. */
export function UserMenu() {
  const me = useSession((s) => s.me);
  const logout = useSession((s) => s.logout);
  const basemapId = useMapStore((s) => s.basemapId);
  const setBasemap = useMapStore((s) => s.setBasemap);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const basemaps = BASEMAPS.filter((b) => import.meta.env.DEV || !b.devOnly);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me) return null;
  const { user } = me;
  // The API sends `user.name`; the hand-written type still says `display_name`. Read whichever is there.
  const raw = user as { name?: unknown; display_name?: unknown };
  const displayName =
    typeof raw.name === 'string' ? raw.name : typeof raw.display_name === 'string' ? raw.display_name : '';
  // The API sends the role beside the user (`me.role`); the hand-written type has it inside. Only admins see the console.
  const isAdmin = (me as { role?: unknown }).role === 'admin' || user.role === 'admin';
  return (
    <div className="mm-user" ref={root}>
      <button
        type="button"
        className="mm-user-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${displayName || user.email}`}
        title={user.email}
        onClick={() => setOpen((o) => !o)}
      >
        {initials(displayName, user.email)}
      </button>
      {open && (
        <div className="mm-menu" role="menu" aria-label="Account">
          <div className="mm-menu-who">
            <strong>{displayName || user.email}</strong>
            <span>{user.email}</span>
          </div>
          <div className="mm-menu-label">Basemap</div>
          {basemaps.map((b) => (
            <button
              key={b.id}
              type="button"
              role="menuitemradio"
              aria-checked={b.id === basemapId}
              className="mm-menu-item"
              onClick={() => setBasemap(b.id)}
            >
              {b.title}
            </button>
          ))}
          <hr />
          {isAdmin && (
            <a role="menuitem" className="mm-menu-item" href="/console" style={{ textDecoration: 'none' }}>
              <Settings2 size={16} aria-hidden="true" />
              Configure this app
            </a>
          )}
          {isAdmin && (
            <a role="menuitem" className="mm-menu-item" href="/access" style={{ textDecoration: 'none' }}>
              <KeyRound size={16} aria-hidden="true" />
              Requests and codes
            </a>
          )}
          <button type="button" role="menuitem" className="mm-menu-item" onClick={() => void logout()}>
            <LogOut size={16} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
